import { PowerballDiscover, PowerballWorkspaceNav, POWERBALL_TIERS } from "./PowerballWorkspace.tsx";
import { PowerballPredictionsPanel } from "./PowerballPredictionsPanel.tsx";
import { usePowerballPredictionFeed } from "../../powerball/usePowerballPredictionFeed.ts";
import { PowerballBooking } from "./PowerballBooking.tsx";
import { PowerballOperationsPanel } from "./PowerballOperationsPanel.tsx";
// POWERBALL workspace: live discovery, prediction boards and immutable publications.

import { Link, useParams, useSearchParams } from "react-router-dom";
import { Icon } from "../../components/icons/Icon.tsx";
import { usePowerballWeek } from "../../powerball/usePowerballWeek.ts";
import { DoctrineSection } from "../../components/DoctrineSection.tsx";
import { useDoctrinePicks } from "../../doctrine/useDoctrinePicks.ts";
import { marketLabel, selectionLabel } from "../../exchange/market-format.ts";
import { selectionProgress, formatPowerballPrice } from "../../powerball/publication.ts";
import { combinedOddsDisplay } from "../../powerball/slipDisplay.ts";
import { getRequiredLegCount, type SlipType } from "../../powerball/ranges.ts";
import { BankToGoalCta } from "../../components/sika-extras/BankToGoalCta.tsx";
import { Perfect10Panel } from "./Perfect10Panel.tsx";
import type { PbSlip, PbSelection } from "../../powerball/usePowerballWeek.ts";
import {
  FpHead,
  FpTabs,
  FpSkeleton,
  FpAwait,
  fmtNum,
  fmtKick,
  leagueCountry,
  countryFromCode,
} from "../football/_fpkit.tsx";
import "../../styles/powerball-premium.css";

// ── Types + helpers ─────────────────────────────────────────

type PbTab = "p10" | "p20" | "mm" | "multiverse" | "lower" | "goals" | "big" | "perfect10";

function multiverseFamily(code: string): string {
  if (code.startsWith("match_winner_")) return "match_result";
  if (code.startsWith("double_chance_")) return "double_chance";
  if (code.startsWith("asian_handicap_")) return "asian_handicap";
  if (code.startsWith("total_goals_")) return "full_time_goals";
  if (code.startsWith("team_goals_")) return "team_goals";
  if (code.startsWith("first_half_total_goals_")) return "first_half_goals";
  if (code.startsWith("second_half_total_goals_")) return "second_half_goals";
  if (code.startsWith("first_half_winner_")) return "first_half_result";
  if (code.startsWith("second_half_winner_")) return "second_half_result";
  if (code.startsWith("btts_")) return "btts";
  if (code.startsWith("total_corners_")) return "match_corners";
  if (/^(home|away)_corners_/.test(code)) return "team_corners";
  if (code.startsWith("corners_ah_")) return "corner_handicap";
  return "other";
}

function tabToSlipType(tab: PbTab): SlipType {
  if (tab === "p10") return "powerball_10";
  if (tab === "p20") return "mega_powerball_20";
  if (tab === "lower") return "lower_lines";
  if (tab === "goals") return "goals_bank";
  if (tab === "big") return "big_stage";
  if (tab === "multiverse") return "multiverse";
  // "perfect10" never reaches a SlipSection (it renders Perfect10Panel), but
  // keep the mapping total for the type-checker.
  return "mixed_mega_jackpot";
}

const SLIP_DISPLAY: Record<PbTab, { title: string; tag: string; tagline: string }> = {
  p10: {
    title: "Powerball 10",
    tag: "10 legs · high conviction",
    tagline: "The daily ten. Every leg counts.",
  },
  p20: {
    title: "Mega 25",
    tag: "25 legs · deep coverage",
    tagline: "Where discipline meets volume.",
  },
  mm: {
    title: "Mixed Mega",
    tag: "45 legs · multi-market",
    tagline: "Every market. Every angle. Maximum diversification.",
  },
  multiverse: {
    title: "MULTIVERSE",
    tag: "35–45 legs · multi-market",
    tagline: "45 fixtures. 45 angles. One slip.",
  },
  lower: {
    title: "Lower Lines",
    tag: "50 legs · same day",
    tagline: "Lower lines across a full matchday.",
  },
  goals: {
    title: "Goals Bank",
    tag: "40 legs · goals only",
    tagline: "A focused goals board across the strongest available fixtures.",
  },
  big: {
    title: "The Big Stage",
    tag: "18–40 legs · UEFA week",
    tagline: "The strongest selections from this week's UEFA fixtures.",
  },
  // perfect10 renders <Perfect10Panel/>, not a SlipSection — this entry only
  // satisfies the Record<PbTab, …> type; its fields are never read.
  perfect10: {
    title: "Perfect 10",
    tag: "10 legs · maximum conviction",
    tagline: "The 10 our model trusts most, distilled across every tier.",
  },
};

function todayTitle(sessionDate?: string | null): string {
  const d = sessionDate ? new Date(`${sessionDate}T12:00:00Z`) : new Date();
  return d.toLocaleDateString("en-GB", {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}

// ── Match hero — picked side in amber ──────────────────────

function matchHero(leg: PbSelection): JSX.Element {
  const match = String(leg.match_name ?? "").trim();
  const m = match.match(/^(.+?)\s+(?:vs?\.?|-|@)\s+(.+)$/i);
  if (!m) return <span>{match || "TBD"}</span>;
  const home = m[1]!.trim();
  const away = m[2]!.trim();
  const pick = (leg.pick ?? "").toLowerCase();
  const homePicked = pick === "home" || pick === home.toLowerCase();
  const awayPicked = pick === "away" || pick === away.toLowerCase();
  return (
    <span>
      <span className={homePicked ? "pickside" : undefined}>{home}</span>
      <span style={{ opacity: 0.5 }}> vs </span>
      <span className={awayPicked ? "pickside" : undefined}>{away}</span>
    </span>
  );
}

// ── Leg card ───────────────────────────────────────────────

export function LegCard({ leg, index, multiverse = false }: { leg: PbSelection; index: number; multiverse?: boolean }): JSX.Element {
  const tone = leg.outcome === "win" ? "tone-won" : leg.outcome === "loss" ? "tone-lost" : "";
  const countryObj =
    countryFromCode(leg.country_code) ?? leagueCountry(leg.league, leg.league_short);
  // Render the country as text (flag + name) — dropping the {flag,name} object
  // straight into ctxParts stringified it as "[object Object]". Every leg must
  // surface a flag so users can locate the league; fall back to 🌍 when the
  // league is genuinely unmapped rather than showing nothing.
  const country = countryObj ? `${countryObj.flag} ${countryObj.name}` : "🌍";
  const teams = String(leg.match_name ?? "").match(/^(.+?)\s+(?:vs?\.?|-|@)\s+(.+)$/i);
  const legContext = teams
    ? { home_team: teams[1]!.trim(), away_team: teams[2]!.trim() }
    : undefined;
  const readableMarket = marketLabel(leg.market);
  const readableSelection = selectionLabel(leg.market, leg.pick, legContext);

  return (
    <article className={`pb-prem-leg pb-publication-row ${tone}`} aria-label={leg.match_name}>
      <span className="pb-ledger-number">{String(index + 1).padStart(2, "0")}</span>
      <div className="pb-ledger-fixture"><strong>{matchHero(leg)}</strong><small>{leg.league} · {country}</small><small>{leg.kickoff_at ? new Intl.DateTimeFormat("en-GB", {timeZone:"Africa/Accra",day:"numeric",month:"short",hour:"2-digit",minute:"2-digit"}).format(new Date(leg.kickoff_at)) : "Kickoff unconfirmed"}</small></div>
      <div className="pb-prem-leg-pickline"><small>{readableMarket}</small><strong>{readableSelection}</strong></div>
      <span className="pb-ledger-odds">{formatPowerballPrice(leg.odds)}</span>
      <span className={`pb-leg-state ${leg.outcome ?? "pending"}`}>{selectionProgress(leg)}</span>
    </article>
  );
}

// ── Slip section (like a Desk formula card) ────────────────

function SlipSection({ slip, tab, targetLegs }: { slip: PbSlip; tab: PbTab; targetLegs?: number }): JSX.Element {
  const display = SLIP_DISPLAY[tab];
  const odds = combinedOddsDisplay(slip);
  const legCount = targetLegs ?? getRequiredLegCount(tabToSlipType(tab));
  const stake = slip.stake_amount;
  const potentialReturn =
    slip.publication_id ? `GH₵${fmtNum(slip.potential_win)}` : odds.kind === "in_range" && odds.value ? `GH₵${fmtNum(Math.round(stake * odds.value))}` : "—";
  const familyCounts = tab === "multiverse"
    ? Object.entries(slip.selections.reduce<Record<string, number>>((counts, leg) => {
      const family = multiverseFamily(leg.market).replaceAll("_", " ");
      counts[family] = (counts[family] ?? 0) + 1;
      return counts;
    }, {})).sort((a, b) => b[1] - a[1])
    : [];

  return (
    <section className="pb-prem-slip">
      <div className="pb-prem-slip-header">
        <div>
          <h2 className="pb-prem-slip-title">{display.title}</h2>
          <p className="pb-prem-slip-tagline">{display.tagline}</p>
          {slip.publication_id && <p className="pb-prem-slip-tagline">{slip.session_date} · Version {slip.publication_version} · Original published ticket · {slip.validation_state === "LEGACY_ARCHIVE" ? "Historical archive · not currently validated" : slip.validation_state === "REVIEWED_PUBLICATION" ? "Reviewed publication" : "Validation pending"}</p>}
          {slip.publication_id && <PowerballBooking key={slip.publication_id} publicationId={slip.publication_id} eligible={slip.execution_eligible === true} />}
          {slip.fixture_window_start && slip.fixture_window_end && <p>Fixture window: {slip.fixture_window_start.slice(0,10)} to {slip.fixture_window_end.slice(0,10)}</p>}
          {slip.publication_mode === "FULL" && <p>Full board · {slip.selections.length} published legs</p>}
          {slip.publication_mode === "REDUCED" && <p>Reduced board · {slip.selections.length} published legs</p>}
          {slip.settlement_state === "PENDING_DATA" && <p>Awaiting verified result data</p>}
          {typeof slip.realized_return === "number" && <p>Settled simulated return: GH₵{fmtNum(slip.realized_return)}</p>}
        </div>
        <span className="pb-prem-slip-meta">{slip.publication_id ? `${slip.selections.length} published legs` : display.tag}</span>
      </div>

      {slip.publication_id && <dl className="pb-publication-meta"><div><dt>Original publication</dt><dd>{slip.original_published_at ? new Date(slip.original_published_at).toLocaleString("en-GB", {timeZone:"Africa/Accra"}) : "Not recorded"}</dd></div><div><dt>Latest publication</dt><dd>{slip.latest_published_at ? new Date(slip.latest_published_at).toLocaleString("en-GB", {timeZone:"Africa/Accra"}) : "Not recorded"}</dd></div><div><dt>Original selections</dt><dd>{slip.selections.length} preserved</dd></div></dl>}
      {typeof slip.void_adjusted_odds === "number" && <p>Void-adjusted combined odds: {formatPowerballPrice(slip.void_adjusted_odds)} · Original odds remain unchanged.</p>}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "baseline",
          flexWrap: "wrap",
          gap: 12,
        }}
      >
        <div>
          <span className="pb-prem-combined">
            {odds.kind === "in_range"
              ? odds.text
              : odds.kind === "out_of_range"
                ? "Rebalance"
                : "—"}
          </span>
          <div className="pb-prem-combined-sub">
            {slip.selections.length} leg{slip.selections.length === 1 ? "" : "s"}
            {" · "}stake GH₵{fmtNum(stake)} · return {potentialReturn}
          </div>
        </div>
        <span
          className="pb-prem-slip-meta"
          style={{
            color:
              slip.status === "published"
                ? "var(--sz-success-text, #5fd39a)"
                : "var(--sz-text-muted)",
          }}
        >
          {slip.validation_state === "LEGACY_ARCHIVE" ? "ARCHIVE" : slip.status === "published" ? "PUBLISHED" : slip.status.toUpperCase().replace(/_/g, " ")}
        </span>
      </div>

      {familyCounts.length > 0 && (
        <div className="pb-prem-leg-context" aria-label="Market mix">
          {familyCounts.map(([family, count]) => `${family} ${count}`).join(" · ")}
        </div>
      )}

      {slip.selections.length === 0 ? (
        <FpAwait
          kicker="ASSEMBLING"
          title={`0 of ${legCount} legs added`}
          body="Pool is being rebalanced. Legs appear once the pipeline assembles enough qualified picks."
          ghosts={Math.min(legCount, 6)}
        />
      ) : (
        <div className="pb-publication-ledger"><div className="pb-ledger-columns" aria-hidden="true"><span>#</span><span>Fixture / competition</span><span>Market / selection</span><span>Original odds</span><span>Status</span></div>
          {slip.selections.map((leg, i) => (
            <LegCard key={leg.id} leg={leg} index={i} multiverse={tab === "multiverse"} />
          ))}
        </div>
      )}

      {slip.result === "jackpot" && typeof slip.realized_return === "number" && (
        <div style={{ paddingTop: 12 }}>
          <BankToGoalCta
            source="powerball"
            sourceId={slip.id}
            amount={slip.realized_return ?? 0}
            sourceLabel={`Powerball ${display.title}`}
          />
        </div>
      )}
    </section>
  );
}

// Exported for backward compat with tests
export const SlipTable = SlipSection;

// ── Page ────────────────────────────────────────────────────

export function PowerballPage(): JSX.Element {
  const params = useParams<{ week?: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const targetDate = searchParams.get("date");
  const targetPublication = searchParams.get("publication");
  const routeWeek = params.week ? Number(params.week) : null;
  const targetWeek = routeWeek !== null && Number.isFinite(routeWeek) ? routeWeek : null;
  const { cores = [], tierContracts = [], reviewPendingTiers = [], weekNumber, pb10, mega20, mixedMega, lowerLines, goalsBank, bigStage, multiverse, loading, error, refetch } =
    usePowerballWeek(targetWeek, { sessionDate: targetDate, publicationId: targetPublication });
  const requestedTab = searchParams.get("tab");
  const tab: PbTab = requestedTab && requestedTab in SLIP_DISPLAY ? requestedTab as PbTab : "p10";
  const setTab = (next: PbTab) => setSearchParams(previous => { const updated = new URLSearchParams(previous); updated.set("tab", next); return updated; });
  const isDiscover = !requestedTab && !targetDate && !targetPublication && targetWeek === null;

  const showCurrentPredictions = !targetDate && !targetPublication && targetWeek === null;
  const { feed: predictionFeed, error: predictionFeedError, refetch: refetchPredictions } = usePowerballPredictionFeed(showCurrentPredictions);

  const {
    probabilityPicks: doctrineProbPicks,
    valuePicks: doctrineValuePicks,
    substrateEmpty,
    substrateWeek,
  } = useDoctrinePicks({ t1HasPicks: !!(pb10 || mega20 || mixedMega || lowerLines || goalsBank || bigStage || multiverse) });

  if (loading && (!showCurrentPredictions || !predictionFeed)) {
    return (
      <div className="fp pb-workspace">
        <FpHead kicker="POWERBALL" badge="DAILY" title={todayTitle(targetDate)} />
        <FpSkeleton rows={4} />
      </div>
    );
  }

  if (error && !showCurrentPredictions) {
    return (
      <div className="fp pb-workspace">
        <FpHead kicker="POWERBALL" badge="DAILY" title={todayTitle(targetDate)} />
        <FpAwait kicker="ERROR" title="Pipeline unavailable" body={error} icon="info" ghosts={0} />
      </div>
    );
  }

  const activeSlip =
    tab === "p10"
      ? pb10
      : tab === "p20"
        ? mega20
        : tab === "mm"
          ? mixedMega
          : tab === "multiverse"
            ? multiverse
          : tab === "lower"
            ? lowerLines
            : tab === "goals"
              ? goalsBank
            : tab === "big"
              ? bigStage
            : null;
  const currentPredictions = predictionFeed?.tiers[tab === "perfect10" ? "perfect10" : tabToSlipType(tab)];
  const feedCount = (tier: string, saved?: number) => showCurrentPredictions ? predictionFeed?.tiers[tier]?.selections.length : saved;

  return (
    <div className="fp pb-workspace">
      <PowerballWorkspaceNav active={isDiscover ? "discover" : "boards"} />
      <header className="pb-workspace-header"><div><span className="pb-eyebrow">FOOTBALL INTELLIGENCE</span><h1>{isDiscover ? "POWERBALL" : POWERBALL_TIERS.find(t => t.tab === tab)?.title}</h1><p>{isDiscover ? "Eight perspectives. One organised workspace." : `${todayTitle(targetDate ?? predictionFeed?.session_date)} · Kickoff times in Accra`}</p></div><button type="button" className="pb-secondary-button" onClick={() => { refetch(); refetchPredictions(); }}><Icon name="refresh" size={14} /> Refresh</button></header>
      {error && <p role="alert" className="pb-notice">Saved tickets could not be loaded. Current predictions refresh separately.</p>}
      {!showCurrentPredictions && reviewPendingTiers.length > 0 && <p role="status" className="pb-notice">Ticket review is pending.</p>}
      {isDiscover ? <PowerballDiscover feed={predictionFeed} error={predictionFeedError} onOpen={next => setTab(next as PbTab)} /> : <>
      <FpTabs<PbTab>
        tabs={[
          { key: "p10", label: "POWERBALL 10", count: feedCount("powerball_10", pb10?.selections.length) },
          { key: "p20", label: "MEGA 25", count: feedCount("mega_powerball_20", mega20?.selections.length) },
          { key: "mm", label: "MIXED MEGA", count: feedCount("mixed_mega_jackpot", mixedMega?.selections.length) },
          { key: "multiverse", label: "MULTIVERSE", count: feedCount("multiverse", multiverse?.selections.length) },
          { key: "perfect10", label: "PERFECT 10", count: feedCount("perfect10") },
          { key: "lower", label: "LOWER LINES", count: feedCount("lower_lines", lowerLines?.selections.length) },
          { key: "goals", label: "GOALS BANK", count: feedCount("goals_bank", goalsBank?.selections.length) },
          { key: "big", label: "THE BIG STAGE", count: feedCount("big_stage", bigStage?.selections.length) },
        ]}
        active={tab}
        onChange={setTab}
      />

      {showCurrentPredictions && <PowerballPredictionsPanel board={currentPredictions} error={predictionFeedError} />}
      {showCurrentPredictions ? (
        <details className="pb-saved-tickets"><summary>{activeSlip?.validation_state === "REVIEWED_PUBLICATION" ? "Saved reviewed ticket" : "Saved tickets and history"}</summary>
          {tab === "perfect10" ? <Perfect10Panel /> : activeSlip ? <SlipSection slip={activeSlip} tab={tab} targetLegs={tierContracts.find(c => c.tier === activeSlip.slip_type)?.target_legs} /> : <p>No saved ticket for this session.</p>}
        </details>
      ) : (
tab === "perfect10" ? (
        <Perfect10Panel />
      ) : activeSlip ? (
        <SlipSection slip={activeSlip} tab={tab} targetLegs={tierContracts.find(c => c.tier === activeSlip.slip_type)?.target_legs} />
      ) : (
        <>
          <FpAwait
            kicker={SLIP_DISPLAY[tab].title.toUpperCase()}
            title={reviewPendingTiers.includes(tabToSlipType(tab)) ? "REVIEW PENDING" : tab === "big" ? "INACTIVE" : "REBALANCING"}
            body={
              tab === "lower"
                ? "The pool targets 50 fixtures. A supported reduced board may publish after the full pool has been checked."
                : tab === "big" ? "No UEFA-week board is currently published for this session." : "No completed board is published for this tier yet."
            }
          />
          <DoctrineSection
            probabilityPicks={doctrineProbPicks}
            valuePicks={doctrineValuePicks}
            isFullFallback={false}
            substrateEmpty={substrateEmpty}
            substrateWeek={substrateWeek}
          />
        </>
      ) 
      )}

      {activeSlip && cores.filter(core => core.source_publication_id === activeSlip.publication_id).map(core => (
        <section key={core.id} className="pb-prem-slip" aria-label={core.label}>
          <h3>{core.label}</h3>
          <p>{core.selections.length} legs · @{formatPowerballPrice(core.combined_odds)} · Source version {core.source_version}</p>
          <div className="pb-prem-legs">{core.selections.map((leg, i) => <LegCard key={leg.id} leg={leg} index={i} />)}</div>
        </section>
      ))}

      </>}
      <details className="pb-workspace-operations"><summary>Workspace operations</summary><div className="pb-board-operations"><PowerballOperationsPanel /></div></details>
      <footer className="pb-workspace-footer"><span>Intelligence, not certainty.</span><Link to="/powerball/history">Season History →</Link><span>All kickoff times · Africa/Accra</span></footer>
    </div>
  );
}
